import React from 'react';
import styles from '../../../styles/ListingManagement.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import type { ListingClient, ListingClientDocument, MaritalStatus } from '../../../types/ListingTypes';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ClientForm {
  name: string;
  surname: string;
  email: string;
  cell: string;
  streetNumber: string;
  streetName: string;
  city: string;
  province: string;
  postalCode: string;
  maritalStatus: MaritalStatus | '';
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const MARITAL_STATUSES: MaritalStatus[] = [
  'Married', 'Single', 'Divorced', 'Separated', 'Widowed',
];

export const PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal',
  'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape',
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  client: ListingClient;
  form: ClientForm;
  expanded: boolean;
  saving: boolean;
  error: string | undefined;
  docError: string | null;
  onToggle: () => void;
  onFieldChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onSave: () => void;
  onDiscard: () => void;
  onDeleteDoc: (docId: string) => void;
  onAddDoc: () => void;
  onRemove: () => void;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function TextField({
  label, name, value, type = 'text', required = true, onChange,
}: {
  label: string;
  name: string;
  value: string;
  type?: string;
  required?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className={styles.categoryRow}>
      <label className={styles.categoryLabel}>
        {label}{required && <span style={{ color: '#dc3545', marginLeft: 2 }}>*</span>}
      </label>
      <div className={styles.categoryInputWrapper}>
        <input
          type={type}
          name={name}
          className={styles.categoryInput}
          value={value}
          onChange={onChange}
          required={required}
        />
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const ClientTile: React.FC<Props> = ({
  client, form, expanded, saving, error, docError,
  onToggle, onFieldChange, onSave, onDiscard, onDeleteDoc, onAddDoc, onRemove,
}) => (
  <div className={styles.transactionTile} style={{ marginBottom: 8 }}>
    {/* Header */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <strong>{client.name} {client.surname}</strong>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          className={`${buttonStyles.button} ${buttonStyles.secondary}`}
          onClick={onToggle}
        >
          {expanded ? 'Collapse' : 'Edit'}
        </button>
        <button
          type="button"
          className={`${buttonStyles.button} ${buttonStyles.danger}`}
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
    </div>

    {/* Expanded edit form */}
    {expanded && (
      <div style={{ marginTop: 12 }}>
        <TextField label="First Name" name="name" value={form.name} onChange={onFieldChange} />
        <TextField label="Surname" name="surname" value={form.surname} onChange={onFieldChange} />
        <TextField label="Email" name="email" value={form.email} onChange={onFieldChange} />
        <TextField label="Cell" name="cell" value={form.cell} onChange={onFieldChange} />
        <TextField label="Street Number" name="streetNumber" value={form.streetNumber} type="number" required={false} onChange={onFieldChange} />
        <TextField label="Street Name" name="streetName" value={form.streetName} required={false} onChange={onFieldChange} />
        <TextField label="City" name="city" value={form.city} required={false} onChange={onFieldChange} />

        <div className={styles.categoryRow}>
          <label className={styles.categoryLabel}>Province</label>
          <div className={styles.categoryInputWrapper}>
            <select
              className={styles.categoryInput}
              name="province"
              value={form.province}
              onChange={onFieldChange}
            >
              <option value="">— Select province —</option>
              {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <TextField label="Postal Code" name="postalCode" value={form.postalCode} required={false} onChange={onFieldChange} />

        <div className={styles.categoryRow}>
          <label className={styles.categoryLabel}>Marital Status</label>
          <div className={styles.categoryInputWrapper}>
            <select
              className={styles.categoryInput}
              name="maritalStatus"
              value={form.maritalStatus}
              onChange={onFieldChange}
            >
              <option value="">— Select —</option>
              {MARITAL_STATUSES.map(ms => (
                <option key={ms} value={ms}>{ms}</option>
              ))}
            </select>
          </div>
        </div>

        {error && <p className={styles.agreementFormError}>{error}</p>}

        <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'space-between', margin: '8px' }}>
          <button
            type="button"
            className={`${buttonStyles.button} ${buttonStyles.primary}`}
            onClick={onSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Client'}
          </button>
          <button
            type="button"
            className={`${buttonStyles.button} ${buttonStyles.secondary}`}
            onClick={onDiscard}
            disabled={saving}
          >
            Discard
          </button>
        </div>

        {/* Documents */}
        <div style={{ marginTop: 20, borderTop: '1px solid #e9ecef', paddingTop: 14 }}>
          <h5 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 10 }}>Documents</h5>
          {(client.documents ?? []).length === 0 && (
            <p style={{ color: '#adb5bd', fontSize: '0.85em', marginBottom: 10 }}>No documents yet.</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {(client.documents ?? []).map((doc: ListingClientDocument) => (
              <div
                key={doc.id}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 10px', background: '#f8f9fa', borderRadius: 6 }}
              >
                <div>
                  <strong style={{ fontSize: '0.9em' }}>{doc.name}</strong>
                  {doc.documentType && (
                    <span style={{ marginLeft: 6, color: '#6c757d', fontSize: '0.8em' }}>
                      ({doc.documentType.name})
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {doc.url && (
                    <a href={doc.url} download={doc.name} className={styles.docDownloadBtn} onClick={e => e.stopPropagation()}>↓</a>
                  )}
                  <button
                    type="button"
                    className={`${buttonStyles.button} ${buttonStyles.danger}`}
                    style={{ padding: '2px 8px', fontSize: '0.8em' }}
                    onClick={() => onDeleteDoc(doc.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
          {docError && <p className={styles.agreementFormError}>{docError}</p>}
          <button
            type="button"
            className={`${buttonStyles.button} ${buttonStyles.primary}`}
            style={{ marginTop: 4 }}
            onClick={onAddDoc}
          >
            + Add Document
          </button>
        </div>
      </div>
    )}
  </div>
);

export default ClientTile;
